import { useState, useMemo, useRef } from 'react';
import API, { fmt } from '@/lib/api';
import { toast } from 'sonner';
import { Sparkle, Plus, X, Trash, Camera } from '@phosphor-icons/react';
import { generateEan13 } from '@/lib/ean';

/**
 * VariantenPanel — alleen zichtbaar wanneer de Varianten-module aan staat in Instellingen.
 *
 * Props:
 *  - artId            : string    — ID van het master-artikel (null tijdens 'nieuw' fase)
 *  - artCode          : string    — master art_code, voor auto-generatie
 *  - artFotoUrl       : string    — fallback foto (master)
 *  - actief           : boolean   — heeft varianten? (per artikel toggle)
 *  - dimensies        : array     — [{naam,label,waardes}]
 *  - varianten        : array     — [{combo_key,combo,art_code,ean,voorraad,prijs_override,foto_url}]
 *  - omschrijving     : string    — voor AI prompt
 *  - categorie        : string    — voor AI prompt
 *  - onChange         : (next) => void   — { actief?, dimensies?, varianten? } merge in form
 *  - onPersist        : () => Promise<void> — opslaan master eerst zodat artId bestaat
 */
export default function VariantenPanel({
  artId, artCode, artFotoUrl, actief, dimensies, varianten,
  omschrijving, categorie, masterVoorraad, onChange, onPersist,
}) {
  const [aiBusy, setAiBusy] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [editVal, setEditVal] = useState({});
  // Verberg varianten met voorraad=0 — default ON, gepersisteerd per gebruiker
  const [verbergLeeg, setVerbergLeeg] = useState(() => {
    try { return localStorage.getItem('var-panel-verberg-leeg') !== '0'; }
    catch { return true; }
  });
  const toggleVerbergLeeg = () => {
    setVerbergLeeg(prev => {
      const next = !prev;
      try { localStorage.setItem('var-panel-verberg-leeg', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  const dims = dimensies || [];
  const vars = varianten || [];

  const setActief = (v) => onChange?.({ actief: v });

  const setDimensies = (next) => onChange?.({ dimensies: next });

  const aiSuggest = async () => {
    if (!omschrijving && !categorie) {
      toast.error('Vul eerst omschrijving of categorie in om AI een suggestie te laten doen.');
      return;
    }
    setAiBusy(true);
    try {
      const r = await API.post('/artikels/genereer-maatreeks', { omschrijving, categorie });
      if (r.data?.ok && r.data.dimensies?.length) {
        setDimensies(r.data.dimensies);
        toast.success(`AI suggereerde ${r.data.dimensies.length} dimensie(s)${r.data.bron === 'fallback' ? ' (fallback)' : ''}`);
      } else {
        toast.error('AI gaf geen suggestie terug');
      }
    } catch (e) {
      toast.error('AI fout', { description: e?.response?.data?.detail || e.message });
    } finally {
      setAiBusy(false);
    }
  };

  const regenereer = async (overschrijf_codes = false) => {
    if (!dims.length || dims.some(d => !(d.waardes || []).length)) {
      toast.error('Definieer eerst de dimensies en hun waardes.');
      return;
    }
    setRegenBusy(true);
    try {
      if (!artId) {
        // Nieuw artikel (nog niet opgeslagen) → stateless genereren in browser/API
        const bestaand = overschrijf_codes
          ? (vars || []).map(v => ({ ...v, art_code: '' }))
          : (vars || []);
        const r = await API.post('/artikels/varianten/genereer', {
          art_code: artCode || 'ART',
          dimensies: dims,
          varianten: bestaand,
        });
        if (r.data?.ok) {
          onChange?.({ actief: true, varianten: r.data.varianten });
          toast.success(`${r.data.aantal} variant(en) klaar — worden opgeslagen bij "Artikel aanmaken"`);
        }
        return;
      }
      // Bestaand artikel → server-side regenereer + auto-persist
      const r = await API.post(`/artikels/${artId}/varianten/regenereer`, { overschrijf_codes });
      if (r.data?.ok) {
        onChange?.({ actief: true, varianten: r.data.varianten });
        toast.success(overschrijf_codes
          ? `${r.data.aantal} variant(en) gegenereerd — alle codes overschreven`
          : `${r.data.aantal} variant(en) gegenereerd`);
      }
    } catch (e) {
      toast.error('Regenereren mislukt', { description: e?.response?.data?.detail || e.message });
    } finally {
      setRegenBusy(false);
    }
  };

  const updateVariant = async (combo_key, partial) => {
    if (!artId) {
      // Lokaal aanpassen wanneer nog niet opgeslagen — wordt later mee gepersisteerd
      const next = vars.map(v => v.combo_key === combo_key ? { ...v, ...partial } : v);
      onChange?.({ varianten: next });
      return;
    }
    try {
      await API.put(`/artikels/${artId}/varianten/${encodeURIComponent(combo_key)}`, partial);
      const next = vars.map(v => v.combo_key === combo_key ? { ...v, ...partial } : v);
      onChange?.({ varianten: next });
    } catch (e) {
      toast.error('Variant-update mislukt', { description: e?.response?.data?.detail || e.message });
    }
  };

  // Bereken voorraadtotaal en aantal varianten met EAN
  const totalen = useMemo(() => {
    let stockTot = 0; let metEan = 0;
    vars.forEach(v => {
      stockTot += parseFloat(v.voorraad) || 0;
      if (v.ean) metEan++;
    });
    return { stockTot, metEan, total: vars.length };
  }, [vars]);

  // Verdeel een totaal-aantal evenredig over alle varianten (rest naar laatste)
  const verdeelTotaal = () => {
    if (!vars.length) { toast.error('Geen varianten om over te verdelen'); return; }
    const inp = window.prompt(`Totaal aantal stuks om te verdelen over ${vars.length} variant(en):`, String(totalen.stockTot || 0));
    if (inp === null) return;
    const totaal = Math.max(0, parseInt(inp, 10) || 0);
    const per = Math.floor(totaal / vars.length);
    const rest = totaal - (per * vars.length);
    const next = vars.map((v, i) => ({
      ...v,
      voorraad: per + (i === vars.length - 1 ? rest : 0),
    }));
    onChange?.({ varianten: next });
    toast.success(`${totaal} stuks verdeeld`, { description: `${per} per variant${rest ? ` (+${rest} op laatste)` : ''}` });
  };

  if (!actief) {
    // Inactief — toon enkel de toggle om varianten te activeren
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-3" data-testid="varianten-panel">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1">
            <div className="text-sm font-bold text-gray-700">🎨 Varianten / Maten</div>
            <div className="text-xs text-gray-500 mt-0.5">
              Activeer om dit artikel in <b>kleur × maat × breedte</b>-combinaties op te delen (bv. T-shirt in 6 maten × 3 kleuren = 18 SKU's).
            </div>
          </div>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => setActief(true)}
            data-testid="art-varianten-activeer"
          >
            Heeft varianten ▾
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border-2 border-blue-200 bg-blue-50/40 rounded-lg p-3" data-testid="varianten-panel-active">
      {/* Kop met statuslijn + AI knop + deactiveren */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div>
          <div className="text-sm font-bold text-blue-900 inline-flex items-center gap-1.5">🎨 Varianten</div>
          <div className="text-[11px] text-gray-600">
            {totalen.total > 0
              ? <>{totalen.total} variant(en) · voorraad totaal <b className="text-blue-700">{totalen.stockTot}</b> · {totalen.metEan} met EAN</>
              : <>Geen varianten yet — definieer dimensies hieronder</>}
          </div>
        </div>
        <div className="flex gap-1.5">
          {totalen.total > 0 && (
            <button
              type="button"
              className="btn btn-outline btn-xs inline-flex items-center gap-1"
              onClick={verdeelTotaal}
              title="Verdeel een totaal aantal stuks evenredig over alle varianten"
              data-testid="art-varianten-verdeel"
            >
              📊 Verdeel evenredig
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline btn-xs inline-flex items-center gap-1"
            onClick={aiSuggest}
            disabled={aiBusy}
            title="AI stelt dimensies + waardes voor o.b.v. omschrijving + categorie"
            data-testid="art-varianten-ai"
          >
            <Sparkle size={11} weight="duotone" />
            {aiBusy ? '…' : 'AI maatreeks'}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-xs text-gray-500 hover:text-red-600"
            onClick={() => {
              if (vars.length > 0 && !window.confirm(`Deactiveren wist ${vars.length} variant(en) niet uit DB maar verbergt ze. Doorgaan?`)) return;
              setActief(false);
            }}
            data-testid="art-varianten-deactiveer"
          >
            <X size={11} /> Deactiveer
          </button>
        </div>
      </div>

      {/* Live totaal-vergelijking met master */}
      {totalen.total > 0 && (
        <MasterStockBadge stockTot={totalen.stockTot} masterVoorraad={masterVoorraad} />
      )}

      {/* Dimensies-editor (max 3) */}
      <div className="space-y-2 mb-3">
        {dims.map((d, idx) => (
          <DimensieRij
            key={idx}
            dim={d}
            onChange={(next) => {
              const arr = [...dims]; arr[idx] = next; setDimensies(arr);
            }}
            onRemove={() => {
              const arr = [...dims]; arr.splice(idx, 1); setDimensies(arr);
            }}
            testIdx={idx}
          />
        ))}
        {dims.length < 3 && (
          <button
            type="button"
            className="btn btn-outline btn-xs inline-flex items-center gap-1"
            onClick={() => setDimensies([...dims, { naam: dims.length === 0 ? 'kleur' : (dims.length === 1 ? 'maat' : 'breedte'), label: dims.length === 0 ? 'Kleur' : (dims.length === 1 ? 'Maat' : 'Breedte'), waardes: [] }])}
            data-testid="art-varianten-add-dim"
          >
            <Plus size={11} /> Dimensie toevoegen ({dims.length}/3)
          </button>
        )}
      </div>

      {/* Genereer-knop */}
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-blue-200">
        <div className="text-[11px] text-gray-600">
          {dims.length > 0
            ? <>Klaar voor genereren: <b>{dims.reduce((acc, d) => acc * Math.max(0, (d.waardes || []).length), 1)}</b> combinatie(s)</>
            : <>Voeg dimensies toe om te beginnen</>}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn btn-outline btn-xs"
            onClick={() => regenereer(true)}
            disabled={!dims.length || regenBusy || !vars.length}
            data-testid="art-varianten-regenereer-overschrijf"
            title="Regenereer alle codes volgens de huidige format-instellingen (overschrijft ook bestaande codes)"
          >
            🔁 Codes vernieuwen
          </button>
          <button
            type="button"
            className="btn btn-primary btn-xs"
            onClick={() => regenereer(false)}
            disabled={!dims.length || regenBusy}
            data-testid="art-varianten-regenereer"
          >
            {regenBusy ? 'Bezig…' : '🔄 Genereer matrix'}
          </button>
        </div>
      </div>

      {/* Varianten-tabel */}
      {vars.length > 0 && (() => {
        const zichtbare = verbergLeeg
          ? vars.filter(v => (parseFloat(v.voorraad) || 0) > 0)
          : vars;
        const verborgenAantal = vars.length - zichtbare.length;
        return (
          <div className="bg-white rounded border border-blue-200 overflow-hidden">
            {/* Filter-toolbar bovenaan tabel */}
            <div className="flex items-center justify-between px-2 py-1.5 bg-gray-50 border-b border-gray-200 text-[11px]">
              <label className="inline-flex items-center gap-1.5 cursor-pointer select-none" data-testid="var-panel-filter-leeg">
                <input
                  type="checkbox"
                  checked={verbergLeeg}
                  onChange={toggleVerbergLeeg}
                  className="w-3 h-3"
                />
                <span className="text-gray-600">Verberg lege varianten (voorraad = 0)</span>
              </label>
              {verborgenAantal > 0 && (
                <span className="text-gray-400 italic" data-testid="var-panel-hidden-count">
                  {verborgenAantal} verborgen · {zichtbare.length} getoond
                </span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                    {dims.map(d => <th key={d.naam} className="px-2 py-1.5 text-left">{d.label || d.naam}</th>)}
                    <th className="px-2 py-1.5 text-left">Code</th>
                    <th className="px-2 py-1.5 text-left">EAN-barcode</th>
                    <th className="px-2 py-1.5 text-right">Voorraad</th>
                    <th className="px-2 py-1.5 text-right">Prijs override</th>
                    <th className="px-2 py-1.5 text-center">Foto</th>
                  </tr>
                </thead>
                <tbody>
                  {zichtbare.map((v) => (
                    <VariantRij
                      key={v.combo_key}
                      variant={v}
                      dims={dims}
                      artFotoUrl={artFotoUrl}
                      editVal={editVal}
                      setEditVal={setEditVal}
                      onUpdate={(partial) => updateVariant(v.combo_key, partial)}
                    />
                  ))}
                  {zichtbare.length === 0 && (
                    <tr>
                      <td colSpan={dims.length + 5} className="px-3 py-6 text-center text-gray-400 italic">
                        Geen varianten met voorraad. Zet de filter uit om lege varianten te tonen.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-2 py-1.5 bg-gray-50 text-[10px] text-gray-500 border-t border-gray-100">
              💡 Klik op de cellen om EAN/voorraad/prijs aan te passen. EAN-barcodes zijn opzoekbaar in POS via barcode-scan.
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function DimensieRij({ dim, onChange, onRemove, testIdx }) {
  const [nieuwWaarde, setNieuwWaarde] = useState('');
  const waardes = dim.waardes || [];

  const addWaarde = () => {
    const v = nieuwWaarde.trim();
    if (!v) return;
    if (waardes.includes(v)) { toast.warning('Waarde bestaat al'); return; }
    onChange({ ...dim, waardes: [...waardes, v] });
    setNieuwWaarde('');
  };

  const removeWaarde = (i) => {
    const arr = [...waardes]; arr.splice(i, 1);
    onChange({ ...dim, waardes: arr });
  };

  return (
    <div className="bg-white border border-gray-200 rounded p-2 flex items-start gap-2" data-testid={`dim-rij-${testIdx}`}>
      <div className="grid grid-cols-2 gap-2 flex-shrink-0" style={{ minWidth: 240 }}>
        <input
          type="text"
          className="field-input text-xs"
          placeholder="naam (bv. kleur)"
          value={dim.naam || ''}
          onChange={(e) => onChange({ ...dim, naam: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
          data-testid={`dim-naam-${testIdx}`}
        />
        <input
          type="text"
          className="field-input text-xs"
          placeholder="label (bv. Kleur)"
          value={dim.label || ''}
          onChange={(e) => onChange({ ...dim, label: e.target.value })}
          data-testid={`dim-label-${testIdx}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap gap-1 mb-1">
          {waardes.map((w, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-mono rounded border border-blue-200" data-testid={`dim-waarde-${testIdx}-${i}`}>
              {w}
              <button type="button" className="text-blue-500 hover:text-red-600" onClick={() => removeWaarde(i)}>
                <X size={9} weight="bold" />
              </button>
            </span>
          ))}
          {waardes.length === 0 && <span className="text-[10px] text-gray-400 italic">geen waardes — voeg minstens 1 toe</span>}
        </div>
        <div className="flex gap-1">
          <input
            type="text"
            className="field-input text-xs"
            placeholder="+ waarde"
            value={nieuwWaarde}
            onChange={(e) => setNieuwWaarde(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addWaarde(); } }}
            style={{ maxWidth: 200 }}
            data-testid={`dim-add-input-${testIdx}`}
          />
          <button type="button" className="btn btn-outline btn-xs" onClick={addWaarde} data-testid={`dim-add-btn-${testIdx}`}>
            <Plus size={11} />
          </button>
        </div>
      </div>
      <button type="button" className="text-gray-400 hover:text-red-600 self-start" onClick={onRemove} title="Dimensie verwijderen" data-testid={`dim-remove-${testIdx}`}>
        <Trash size={13} />
      </button>
    </div>
  );
}

function VariantRij({ variant, dims, artFotoUrl, editVal, setEditVal, onUpdate }) {
  const fileRef = useRef(null);
  const v = variant;
  const fotoSrc = v.foto_url
    ? (v.foto_url.startsWith('http') ? v.foto_url : `${process.env.REACT_APP_BACKEND_URL}${v.foto_url}`)
    : (artFotoUrl ? (artFotoUrl.startsWith('http') ? artFotoUrl : `${process.env.REACT_APP_BACKEND_URL}${artFotoUrl}`) : null);

  const eanKey = `${v.combo_key}_ean`;
  const voorraadKey = `${v.combo_key}_voorraad`;
  const prijsKey = `${v.combo_key}_prijs`;
  const codeKey = `${v.combo_key}_code`;

  const fotoUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        await onUpdate({ foto_url: ev.target.result });
        toast.success('Foto bijgewerkt');
      } catch (err) {
        toast.error('Foto-upload mislukt');
      }
    };
    reader.readAsDataURL(f);
  };

  return (
    <tr className="border-b border-gray-100 hover:bg-blue-50/30" data-testid={`variant-rij-${v.combo_key}`}>
      {dims.map(d => (
        <td key={d.naam} className="px-2 py-1.5 font-mono text-[11px] text-gray-900 whitespace-nowrap">
          {(v.combo || {})[d.naam] || '—'}
        </td>
      ))}
      <td className="px-2 py-1.5">
        <input
          type="text"
          className="text-[10px] font-mono w-full bg-transparent border border-transparent hover:border-gray-300 focus:border-blue-400 px-1 py-0.5 rounded"
          value={editVal[codeKey] !== undefined ? editVal[codeKey] : (v.art_code || '')}
          onChange={(e) => setEditVal(p => ({ ...p, [codeKey]: e.target.value }))}
          onBlur={() => {
            if (editVal[codeKey] !== undefined && editVal[codeKey] !== v.art_code) {
              onUpdate({ art_code: editVal[codeKey] });
            }
            setEditVal(p => { const np = { ...p }; delete np[codeKey]; return np; });
          }}
          data-testid={`var-code-${v.combo_key}`}
        />
      </td>
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-0.5">
          <input
            type="text"
            className="text-[10px] font-mono w-full bg-transparent border border-transparent hover:border-gray-300 focus:border-blue-400 px-1 py-0.5 rounded"
            placeholder="—"
            value={editVal[eanKey] !== undefined ? editVal[eanKey] : (v.ean || '')}
            onChange={(e) => setEditVal(p => ({ ...p, [eanKey]: e.target.value.replace(/[^0-9]/g, '') }))}
            onBlur={() => {
              const newVal = editVal[eanKey];
              if (newVal !== undefined && newVal !== v.ean) onUpdate({ ean: newVal });
              setEditVal(p => { const np = { ...p }; delete np[eanKey]; return np; });
            }}
            maxLength={13}
            data-testid={`var-ean-${v.combo_key}`}
          />
          {!v.ean && (
            <button
              type="button"
              className="text-[9px] text-blue-600 hover:text-blue-800 px-1"
              title="Genereer EAN-13"
              onClick={() => onUpdate({ ean: generateEan13() })}
              data-testid={`var-ean-gen-${v.combo_key}`}
            >🎲</button>
          )}
        </div>
      </td>
      <td className="px-2 py-1.5 text-right">
        <input
          type="number"
          step="1"
          className="text-[11px] font-mono w-16 text-right bg-transparent border border-transparent hover:border-gray-300 focus:border-blue-400 px-1 py-0.5 rounded"
          value={editVal[voorraadKey] !== undefined ? editVal[voorraadKey] : (v.voorraad ?? 0)}
          onChange={(e) => setEditVal(p => ({ ...p, [voorraadKey]: e.target.value }))}
          onBlur={() => {
            const nv = editVal[voorraadKey];
            if (nv !== undefined && parseFloat(nv) !== parseFloat(v.voorraad)) {
              onUpdate({ voorraad: parseFloat(nv) || 0 });
            }
            setEditVal(p => { const np = { ...p }; delete np[voorraadKey]; return np; });
          }}
          data-testid={`var-voorraad-${v.combo_key}`}
        />
        {v.voorraad === 0 && <span className="text-[8px] text-red-600 ml-1">leeg</span>}
        {v.voorraad > 0 && v.voorraad < 5 && <span className="text-[8px] text-amber-600 ml-1">laag</span>}
      </td>
      <td className="px-2 py-1.5 text-right">
        <input
          type="number"
          step="0.01"
          className="text-[11px] font-mono w-20 text-right bg-transparent border border-transparent hover:border-gray-300 focus:border-blue-400 px-1 py-0.5 rounded"
          placeholder="—"
          value={editVal[prijsKey] !== undefined ? editVal[prijsKey] : (v.prijs_override ?? '')}
          onChange={(e) => setEditVal(p => ({ ...p, [prijsKey]: e.target.value }))}
          onBlur={() => {
            const nv = editVal[prijsKey];
            if (nv !== undefined) {
              const parsed = nv === '' ? null : parseFloat(nv);
              if (parsed !== v.prijs_override) onUpdate({ prijs_override: parsed });
            }
            setEditVal(p => { const np = { ...p }; delete np[prijsKey]; return np; });
          }}
          data-testid={`var-prijs-${v.combo_key}`}
        />
      </td>
      <td className="px-2 py-1.5 text-center">
        <div className="inline-flex items-center gap-1">
          {fotoSrc ? (
            <img src={fotoSrc} alt="" className="w-7 h-7 object-cover rounded border border-gray-200" />
          ) : (
            <div className="w-7 h-7 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
              <Camera size={10} className="text-gray-400" />
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={fotoUpload} data-testid={`var-foto-input-${v.combo_key}`} />
          <button
            type="button"
            className="text-[9px] text-blue-600 hover:text-blue-800 px-1"
            onClick={() => fileRef.current?.click()}
            title="Eigen variant-foto uploaden"
            data-testid={`var-foto-btn-${v.combo_key}`}
          >📷</button>
          {v.foto_url && (
            <button
              type="button"
              className="text-[9px] text-gray-400 hover:text-red-600 px-1"
              onClick={() => onUpdate({ foto_url: '' })}
              title="Variant-foto verwijderen"
            >×</button>
          )}
        </div>
      </td>
    </tr>
  );
}

// Hulpfunctie: gebruikt ergens? Re-export voor extern gebruik.
export function variantPrijs(master, variant) {
  // Welke prijs gebruiken voor een variant in POS/document?
  if (variant && variant.prijs_override !== null && variant.prijs_override !== undefined) {
    return parseFloat(variant.prijs_override) || 0;
  }
  // Fallback: master basic prijs
  return parseFloat((master?.prijzen || {}).basic) || parseFloat(master?.verkoopprijs) || 0;
}

export function variantLabel(master, variant) {
  if (!variant) return master?.omschrijving || '';
  const parts = Object.entries(variant.combo || {}).map(([k, v]) => v).join(' / ');
  return `${master?.omschrijving || ''}${parts ? ' · ' + parts : ''}`;
}

// Voor ESLint: voorkom unused import warning op fmt
void fmt;

function MasterStockBadge({ stockTot, masterVoorraad }) {
  const master = parseFloat(masterVoorraad);
  const heeftMaster = !isNaN(master) && masterVoorraad !== null && masterVoorraad !== undefined && masterVoorraad !== '';
  const klopt = heeftMaster && Math.abs(master - stockTot) < 0.001;
  // Als er geen master-waarde is ingevuld → toon enkel het totaal (geen vergelijk)
  if (!heeftMaster) {
    return (
      <div className="mb-3 px-2 py-1.5 bg-blue-100 border border-blue-300 rounded text-[11px] text-blue-900 flex items-center gap-2" data-testid="master-stock-badge-info">
        <span>📦</span>
        <span>Som varianten: <b>{stockTot}</b> stuks — wordt automatisch master-voorraad bij opslaan</span>
      </div>
    );
  }
  return (
    <div
      className={`mb-3 px-2 py-1.5 rounded text-[11px] flex items-center gap-2 border ${
        klopt
          ? 'bg-green-50 border-green-300 text-green-800'
          : 'bg-amber-50 border-amber-300 text-amber-800'
      }`}
      data-testid="master-stock-badge"
    >
      <span>{klopt ? '✓' : '⚠'}</span>
      <span>
        Master-voorraad: <b>{master}</b> · Som varianten: <b>{stockTot}</b>
        {!klopt && (
          <> — verschil <b>{(stockTot - master).toFixed(0)}</b></>
        )}
        {klopt && <> — alles in sync</>}
      </span>
    </div>
  );
}
